/**
 * Photo date extraction utility
 *
 * Extracts the original capture date from image EXIF metadata.
 * Must be called BEFORE image compression, as compression strips EXIF data.
 *
 * Uses Amsterdam timezone (Europe/Amsterdam) for display formatting.
 */

import exifr from "exifr";
import { formatInTimeZone } from "date-fns-tz";
import { nl } from "date-fns/locale";

/** Amsterdam timezone constant */
const TIMEZONE = "Europe/Amsterdam";

/**
 * Extract the original capture date from an image file's EXIF metadata
 *
 * @param file - Original image file (before compression)
 * @returns Date object if EXIF date found, null otherwise
 */
export async function extractPhotoDate(file: File): Promise<Date | null> {
  try {
    const exif = await exifr.parse(file, {
      // Only extract date-related fields for efficiency
      pick: ["DateTimeOriginal", "CreateDate", "ModifyDate"],
      // Convert EXIF date strings to Date objects
      reviveValues: true,
    });

    if (!exif) {
      return null;
    }

    // Prefer DateTimeOriginal (when photo was taken), fall back to CreateDate
    const date = exif.DateTimeOriginal || exif.CreateDate;

    if (!date) {
      return null;
    }

    // Ensure we have a valid Date object
    return date instanceof Date ? date : new Date(date);
  } catch {
    // Silently fail for images without EXIF (screenshots, web images, etc.)
    return null;
  }
}

/**
 * Format a photo date for display in Amsterdam timezone with Dutch locale
 *
 * @param date - Date to format
 * @returns Formatted string like "15 jan 2024, 14:30"
 */
export function formatPhotoDate(date: Date): string {
  return formatInTimeZone(date, TIMEZONE, "d MMM yyyy, HH:mm", { locale: nl });
}
